import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/api';
import signalRService from '../services/signalRService';
import { scheduleIdle } from '../utils/scheduleIdle';

const POLL_INTERVAL = 60000;

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    // Bumped when new notifications arrive; open lists reload on change.
    const [version, setVersion] = useState(0);
    const countRef = useRef(0);

    const applyCount = useCallback((count, force = false) => {
        if (force || count > countRef.current) setVersion(v => v + 1);
        countRef.current = count;
        setUnreadCount(count);
    }, []);

    const refreshUnread = useCallback(async () => {
        if (!user) {
            applyCount(0);
            return;
        }
        try {
            const { data } = await notificationService.getUnreadCount();
            applyCount(data.count);
        } catch {
            // silently ignore
        }
    }, [user, applyCount]);

    useEffect(() => {
        const cancelInitialRefresh = scheduleIdle(refreshUnread, 1600);
        if (!user) return cancelInitialRefresh;

        const timer = setInterval(refreshUnread, POLL_INTERVAL);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') refreshUnread();
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            cancelInitialRefresh();
            clearInterval(timer);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [user, refreshUnread]);

    useEffect(() => {
        if (!user) return;

        const handleChanged = (count) => applyCount(count, true);
        signalRService.on('NotificationsChanged', handleChanged);

        return () => signalRService.off('NotificationsChanged', handleChanged);
    }, [user, applyCount]);

    const markRead = useCallback(async (id) => {
        try {
            await notificationService.markRead(id);
            applyCount(Math.max(0, countRef.current - 1));
        } catch {
            // silently ignore
        }
    }, [applyCount]);

    const markAllRead = useCallback(async () => {
        try {
            await notificationService.markAllRead();
            applyCount(0);
        } catch {
            // silently ignore
        }
    }, [applyCount]);

    return (
        <NotificationContext.Provider value={{ unreadCount, version, refreshUnread, markRead, markAllRead }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => useContext(NotificationContext);
